"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Apple-like Dialog
 * - Softer overlay with blur
 * - Glass/elevated variants
 * - Larger round corners, natural shadow
 * - Smooth scale/opacity animations
 *
 * API additions:
 *  <DialogContent variant="glass" size="lg" showCloseButton />
 *  variants: "glass" | "elevated" | "plain"
 *  sizes: "sm" | "md" | "lg" | "xl"
 */

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger(
  props: React.ComponentProps<typeof DialogPrimitive.Trigger>
) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal(
  props: React.ComponentProps<typeof DialogPrimitive.Portal>
) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose(
  props: React.ComponentProps<typeof DialogPrimitive.Close>
) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // fade with soft blur
        "fixed inset-0 z-50 bg-black/45 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

type ContentVariant = "glass" | "elevated" | "plain"
type ContentSize = "sm" | "md" | "lg" | "xl"

const contentVariant: Record<ContentVariant, string> = {
  glass:
    "bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-2xl rounded-3xl",
  elevated:
    "bg-white ring-1 ring-black/5 shadow-2xl rounded-2xl",
  plain:
    "bg-background border shadow-lg rounded-xl", // closest to your original
}

const contentSize: Record<ContentSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  variant = "glass",
  size = "lg",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  variant?: ContentVariant
  size?: ContentSize
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // positioning
          "fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2",
          // animation (soft scale + fade)
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
          // spacing
          "p-0",
          // variant + size
          contentVariant[variant],
          contentSize[size],
          className
        )}
        {...props}
      >
        {/* inner padding container so glass ring stays crisp on edges */}
        <div className="p-6 sm:p-6">
          {children}
        </div>

        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={cn(
              // circular, subtle glass button (darker by default)
              "absolute right-3.5 top-3.5 inline-grid place-items-center",
              "rounded-full p-2",
              "bg-black/10 hover:bg-black/15 backdrop-blur-sm",
              "ring-1 ring-black/10",
              "text-black hover:text-gray-900",
              "transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
              " :bg-white/15 :text-white  :ring-white/25 :ring-offset-neutral-900"
            )}
          >
            <XIcon className="size-4 text-black" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-2 text-center sm:text-left",
        className
      )}
      {...props}
    />
  )
}

/**
 * DialogFooter
 * - Keeps layout responsive
 * - Ensures *ghost* buttons are darker & readable by default inside dialogs.
 *
 * We target common ghost patterns:
 *  - buttons with data-variant="ghost" (some setups add this)
 *  - buttons with class "variant-ghost" (if you add it)
 *  - buttons that are transparent (bg-transparent) which is typical ghost look
 *
 * These selectors won’t affect your solid/primary buttons.
 */
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        // darker ghost buttons inside dialog
        // data-variant="ghost"
        "[&_button[data-variant='ghost']]:text-gray-800",
        "[&_button[data-variant='ghost']]:hover:text-gray-900",
        "[&_button[data-variant='ghost']]:hover:bg-gray-100",
        // .variant-ghost
        "[&_button.variant-ghost]:text-gray-800",
        "[&_button.variant-ghost]:hover:text-gray-900",
        "[&_button.variant-ghost]:hover:bg-gray-100",
        // transparent-looking buttons (typical ghost)
        "[&_button.bg-transparent]:text-gray-800",
        "[&_button.bg-transparent]:hover:text-gray-900",
        "[&_button.bg-transparent]:hover:bg-gray-100",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg sm:text-xl font-semibold leading-none tracking-tight",
        "text-gray-900",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-gray-600/70",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
